// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: team threads in the app (docs/konvo-agents/DESIGN.md, "Team sharing"). What is shared with
// the person ("Shared with you"), each open thread's sharing (people, who is here, comments pinned to
// turns, prompts waiting for the owner), and what the owner shares (Settings). The shared thread
// itself is read like the owner's, through the same routes: Konvo checks the share on every call.
// KONVO_AGENTS_UPDATE kinds `share` and `shared` keep it fresh.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import Agents from '@app/features/konvo_agents/state/Agents';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import type {RestResponse} from '@app/features/platform/types/TransportTypes';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import Users from '@app/features/user/state/Users';
import type {AgentBrowserLimit} from '@konvo/schema/src/domains/konvo/AgentBrowser';
import type {
	AgentOwnerShares,
	AgentShareComment,
	AgentSharedThread,
	AgentSharedWithMe,
	AgentShareLevel,
	AgentSharePrompt,
	AgentThreadShare,
} from '@konvo/schema/src/domains/konvo/AgentShares';
import type {AgentMachineView, AgentsUpdateEvent} from '@konvo/schema/src/domains/konvo/Agents';
import {i18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {makeAutoObservable, observable, reaction, runInAction} from 'mobx';

const BACK_ONLINE_DESCRIPTOR = msg({
	message: "{name}'s {machine} is back online",
	comment:
		'Toast when the machine of a teammate who shared an agent thread with you connects again ("Tell me when it\'s back"). {name} is the owner\'s first name, {machine} the machine.',
});

const key = (machineId: string, threadId: string) => `${machineId}\u0000${threadId}`;

function answered<R extends RestResponse<unknown>>(response: R): R {
	if (!response.ok) throw new HttpError({method: 'GET', path: '', status: response.status, body: response.body});
	return response;
}

/** First name, for short lines ("Waiting for JP", "JP's interns"). */
export const firstNameOf = (name: string) => name.trim().split(/\s+/)[0] || name;

/** The comment composer's pinned turn: the reply a new comment goes on. */
export interface CommentTarget {
	machineId: string;
	threadId: string;
	turnId: string;
	turnAt: string;
	/** Replying to this comment. */
	parentId?: string;
}

/**
 * The owner's machine as a teammate sees it, for the thread view: their own limits on it, so Stop,
 * answers and Allow/Deny show only when the share (and the machine) allow them; never settle,
 * rename, archive, delete, start or pick a model.
 */
export function sharedMachineView(
	entry: AgentSharedThread,
	share: AgentThreadShare | null,
	ownerMachineName: string,
): AgentMachineView {
	const level = share?.me?.level ?? entry.level;
	const approval = share?.me?.approval ?? entry.approval;
	const full = level === 'prompt' && !approval;
	const send = share ? share.machine.send : true;
	const approve = share ? share.machine.approve : false;
	return {
		id: entry.machineId,
		name: ownerMachineName,
		platform: 'other',
		online: share?.machine.online ?? entry.online,
		lastSeenAt: share?.machine.lastSeenAt ?? entry.lastSeenAt,
		connectedAt: '',
		bridgeVersion: '',
		tools: [
			{
				tool: 't3',
				name: entry.tool ?? 'T3 Code',
				version: '',
				// Files: the owner's machine decides (konvo-agents 0.26.0+); Konvo scopes what a teammate reaches.
				can: {
					send: full,
					start: false,
					interrupt: full,
					approve: full,
					settle: false,
					rename: false,
					archive: false,
					delete: false,
					diff: true,
					files: true,
					// Dismissing a question is like answering it: Prompt, straight; the owner's bridge must know how.
					dismissQuestion: full && share?.machine.dismissQuestion === true,
				},
			},
		],
		limits: {projects: 'all', send: full && send, approve: full && approve},
		projects: entry.summary ? [{id: entry.summary.projectId, title: entry.project ?? entry.summary.projectId}] : [],
		counts: {running: 0, needsYou: 0},
	};
}

export class AgentSharesStore {
	/** Threads shared with the person; null until loaded. */
	shared: Array<AgentSharedThread> | null = null;
	sharedError: unknown = null;
	/** Each open thread's sharing. */
	readonly threads = observable.map<string, AgentThreadShare>();
	/** Threads whose share ended while open (removed, sharing switched off, thread gone). */
	readonly gone = observable.set<string>();
	/** What the owner shares, per machine (Settings); null until loaded. */
	owner: AgentOwnerShares | null = null;
	/** The reply a new comment goes on, while the comment composer is open. */
	commentTarget: CommentTarget | null = null;
	/** Phones: the thread whose share sheet is open (from the thread's ⋯ sheet). */
	shareSheet: string | null = null;
	/** Teammates' machines the person asked to hear about ("Tell me when it's back"). */
	readonly waitingFor = observable.set<string>();
	private sharedRequest: Promise<void> | null = null;

	constructor() {
		makeAutoObservable<this, 'sharedRequest'>(this, {sharedRequest: false}, {autoBind: true});
		reaction(
			() => Users.currentUserId,
			(next, previous) => {
				if (next !== previous) this.reset();
			},
		);
	}

	// ── Reads ────────────────────────────────────────────────────────────

	get hasShared(): boolean {
		return (this.shared?.length ?? 0) > 0;
	}

	sharedThread(machineId: string | null | undefined, threadId: string | null | undefined): AgentSharedThread | null {
		if (!machineId || !threadId) return null;
		return this.shared?.find((entry) => entry.machineId === machineId && entry.threadId === threadId) ?? null;
	}

	/** Shared with you, grouped by owner and machine, in the order they were shared. */
	get groups(): Array<{
		key: string;
		owner: AgentSharedThread['owner'];
		machineName: string;
		online: boolean;
		lastSeenAt: string | null;
		threads: Array<AgentSharedThread>;
	}> {
		const out = new Map<
			string,
			{
				key: string;
				owner: AgentSharedThread['owner'];
				machineName: string;
				online: boolean;
				lastSeenAt: string | null;
				threads: Array<AgentSharedThread>;
			}
		>();
		for (const entry of this.shared ?? []) {
			const groupKey = `${entry.owner.userId}/${entry.machineId}`;
			const group = out.get(groupKey) ?? {
				key: groupKey,
				owner: entry.owner,
				machineName: entry.machineName,
				online: entry.online,
				lastSeenAt: entry.lastSeenAt,
				threads: [],
			};
			group.threads.push(entry);
			out.set(groupKey, group);
		}
		return [...out.values()];
	}

	threadShare(machineId: string, threadId: string): AgentThreadShare | null {
		return this.threads.get(key(machineId, threadId)) ?? null;
	}

	isGone(machineId: string, threadId: string): boolean {
		return this.gone.has(key(machineId, threadId));
	}

	commentsOn(machineId: string, threadId: string, turnId: string): Array<AgentShareComment> {
		return (this.threadShare(machineId, threadId)?.comments ?? []).filter((comment) => comment.turnId === turnId);
	}

	/** "<First name>'s <machine>", how a teammate names the owner's machine. */
	ownerMachineName(entry: Pick<AgentSharedThread, 'owner' | 'machineName'>): string {
		return i18n._(
			msg({
				message: "{name}'s {machine}",
				comment: 'Agents: a teammate’s machine, e.g. "JP\'s interns". {name} is the owner’s first name.',
			}),
			{name: firstNameOf(entry.owner.name), machine: entry.machineName},
		);
	}

	// ── Loading ──────────────────────────────────────────────────────────

	loadShared(): Promise<void> {
		this.sharedRequest ??= this.fetchShared().finally(() => {
			this.sharedRequest = null;
		});
		return this.sharedRequest;
	}

	private async fetchShared(): Promise<void> {
		try {
			const response = answered(await http.get<AgentSharedWithMe>(Endpoints.KONVO_AGENTS_SHARED, {mode: 'silent'}));
			runInAction(() => {
				const before = this.shared;
				this.shared = response.body.threads;
				this.sharedError = null;
				// "Tell me when it's back" for a teammate's machine.
				for (const entry of response.body.threads) {
					if (!entry.online || !this.waitingFor.has(entry.machineId)) continue;
					const was = before?.find((old) => old.machineId === entry.machineId);
					if (was?.online) continue;
					this.waitingFor.delete(entry.machineId);
					ToastCommands.success(
						i18n._(BACK_ONLINE_DESCRIPTOR, {name: firstNameOf(entry.owner.name), machine: entry.machineName}),
					);
				}
			});
		} catch (error) {
			runInAction(() => {
				this.sharedError = error;
				this.shared ??= [];
			});
		}
	}

	async loadThread(machineId: string, threadId: string): Promise<void> {
		const k = key(machineId, threadId);
		try {
			const response = await http.get<AgentThreadShare>(Endpoints.KONVO_AGENTS_THREAD_SHARE(machineId, threadId), {
				mode: 'silent',
			});
			runInAction(() => {
				if (response.ok) {
					this.threads.set(k, response.body);
					this.gone.delete(k);
				} else if (response.status === 404) {
					const had = this.threads.get(k);
					this.threads.delete(k);
					// A teammate's share that ended; an owner's thread just has no sharing yet.
					if (had?.role === 'teammate' || this.sharedThread(machineId, threadId)) this.gone.add(k);
				}
			});
		} catch {
			// The next event or the 30 s refresh tries again.
		}
	}

	async loadOwner(): Promise<void> {
		try {
			const response = answered(await http.get<AgentOwnerShares>(Endpoints.KONVO_AGENTS_SHARES, {mode: 'silent'}));
			runInAction(() => {
				this.owner = response.body;
			});
		} catch {}
	}

	// ── The owner shares ─────────────────────────────────────────────────

	async setPerson(
		machineId: string,
		threadId: string,
		userId: string,
		level: AgentShareLevel,
		approval?: boolean,
	): Promise<void> {
		const response = await http.put<AgentThreadShare>(
			Endpoints.KONVO_AGENTS_THREAD_SHARE_PERSON(machineId, threadId, userId),
			{body: {level, ...(approval !== undefined ? {approval} : {})}},
		);
		runInAction(() => this.threads.set(key(machineId, threadId), response.body));
	}

	async removePerson(machineId: string, threadId: string, userId: string): Promise<void> {
		await http.delete(Endpoints.KONVO_AGENTS_THREAD_SHARE_PERSON(machineId, threadId, userId));
		await this.loadThread(machineId, threadId);
	}

	/** A teammate leaves a thread shared with them. */
	async leave(machineId: string, threadId: string): Promise<void> {
		const me = Users.currentUserId;
		if (!me) return;
		await http.delete(Endpoints.KONVO_AGENTS_THREAD_SHARE_PERSON(machineId, threadId, me));
		runInAction(() => {
			this.threads.delete(key(machineId, threadId));
			this.shared = (this.shared ?? []).filter(
				(entry) => !(entry.machineId === machineId && entry.threadId === threadId),
			);
		});
	}

	/** The owner's settings for a machine, kept by Konvo: team sharing and Allow / Deny from Konvo. */
	async saveMachineSettings(
		machineId: string,
		patch: {
			share?: AgentOwnerShares['machines'][number]['sharing'];
			approve?: boolean;
			browser?: AgentBrowserLimit;
			terminals?: boolean;
		},
	): Promise<void> {
		const response = await http.patch<AgentMachineView>(Endpoints.KONVO_AGENTS_MACHINE_SETTINGS(machineId), {
			body: patch,
		});
		Agents.applyMachine(response.body);
		await this.loadOwner();
	}

	async stopAll(machineId: string): Promise<void> {
		await http.post(Endpoints.KONVO_AGENTS_SHARING_STOP(machineId), {body: {}});
		await this.loadOwner();
	}

	// ── Comments ─────────────────────────────────────────────────────────

	pickTurn(target: CommentTarget | null): void {
		this.commentTarget = target;
	}

	async comment(target: CommentTarget, text: string): Promise<void> {
		const {machineId, threadId, turnId, turnAt, parentId} = target;
		const response = await http.post<AgentShareComment>(
			Endpoints.KONVO_AGENTS_THREAD_SHARE_COMMENTS(machineId, threadId),
			{
				body: {text, turnId, turnAt, ...(parentId ? {parentId} : {})},
			},
		);
		runInAction(() => {
			const current = this.threads.get(key(machineId, threadId));
			if (current && !current.comments.some((comment) => comment.id === response.body.id))
				this.threads.set(key(machineId, threadId), {...current, comments: [...current.comments, response.body]});
		});
	}

	async resolve(machineId: string, threadId: string, commentId: string, resolved: boolean): Promise<void> {
		await http.post(Endpoints.KONVO_AGENTS_THREAD_SHARE_COMMENT(machineId, threadId, commentId, 'resolve'), {
			body: {resolved},
		});
		await this.loadThread(machineId, threadId);
	}

	async sendComment(machineId: string, threadId: string, commentId: string): Promise<void> {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		await http.post(Endpoints.KONVO_AGENTS_THREAD_SHARE_COMMENT(machineId, threadId, commentId, 'send'), {
			body: {timeZone},
		});
		await this.loadThread(machineId, threadId);
	}

	// ── Prompts waiting for the owner ────────────────────────────────────

	async askToPrompt(machineId: string, threadId: string, prompt: string): Promise<void> {
		const response = await http.post<AgentSharePrompt>(
			Endpoints.KONVO_AGENTS_THREAD_SHARE_PROMPTS(machineId, threadId),
			{
				body: {prompt},
			},
		);
		runInAction(() => {
			const current = this.threads.get(key(machineId, threadId));
			if (current && !current.prompts.some((entry) => entry.id === response.body.id))
				this.threads.set(key(machineId, threadId), {...current, prompts: [...current.prompts, response.body]});
		});
	}

	async decide(machineId: string, threadId: string, promptId: string, decision: 'approve' | 'decline'): Promise<void> {
		await http.post(Endpoints.KONVO_AGENTS_THREAD_SHARE_PROMPT(machineId, threadId, promptId), {body: {decision}});
		await this.loadThread(machineId, threadId);
	}

	async withdraw(machineId: string, threadId: string, promptId: string): Promise<void> {
		await http.delete(Endpoints.KONVO_AGENTS_THREAD_SHARE_PROMPT(machineId, threadId, promptId));
		await this.loadThread(machineId, threadId);
	}

	openShareSheet(threadId: string | null): void {
		this.shareSheet = threadId;
	}

	notifyWhenBack(machineId: string): void {
		this.waitingFor.add(machineId);
	}

	// ── Realtime ─────────────────────────────────────────────────────────

	applyEvent(event: Extract<AgentsUpdateEvent, {kind: 'share' | 'shared'}>): void {
		if (event.kind === 'shared') {
			void this.loadShared();
			if (this.owner) void this.loadOwner();
			// Open shared threads re-read their sharing (a level or machine change).
			for (const share of this.threads.values())
				if (share.role === 'teammate') void this.loadThread(share.machine.id, share.thread.id);
			return;
		}
		if (this.threads.has(key(event.machineId, event.threadId)) || this.sharedThread(event.machineId, event.threadId))
			void this.loadThread(event.machineId, event.threadId);
		if (this.owner) void this.loadOwner();
	}

	reset(): void {
		this.shared = null;
		this.sharedError = null;
		this.threads.clear();
		this.gone.clear();
		this.owner = null;
		this.commentTarget = null;
		this.shareSheet = null;
		this.waitingFor.clear();
		this.sharedRequest = null;
	}
}

export default new AgentSharesStore();
